import compact from 'lodash/compact';
import first from 'lodash/first';
import last from 'lodash/last';
import { observer } from 'mobx-react';

import style from './TaskParticipants.module.scss';

import { TaskSection } from '../TaskSection';
import { TaskInputSubstitutor } from '../TaskInputSubstitutor';
import { TaskFieldView } from '../TaskFieldView';
import { ParticipantSelect } from './ParticipantSelect';
import { TaskAttributes } from 'chaika-test-attributes';
import { translate } from '@chaika/i18n/helper';
import { SaveButton } from '@components/ActionButtons/SaveButton';
import { ButtonModifiers, ButtonTheme } from '@components/Button';
import { Select } from '@components/Select';
import { SelectionButtons, SelectionButtonsModifiers } from '@components/SelectionButtons';

import { useTaskParticipants } from './hooks/useTaskParticipants';
import { useTaskPractice } from './hooks/useTaskPractice';

export const TaskParticipants = observer((): React.ReactElement<any> => {
    const {
        isCreationMode,
        isEditMode,
        canEdit,
        isPracticeAssignee,
        assigneeTypeOptions,
        assigneeType,
        defaultUsers,
        authorData,
        executorData,
        onAssigneeTypeSelect,
        onExecutorsSelect,
        setOnlyMe,
    } = useTaskParticipants();
    const { practiceOptions, selectedPracticeId, practiceNames, onPracticeSelect } = useTaskPractice();

    const [, authorName] = authorData;
    const executorIds = compact(executorData.map(first));
    const executorNames = compact(executorData.map(last));

    return (
        <TaskSection>
            {!isCreationMode && !isEditMode && (
                <TaskFieldView
                    label={translate('taskAuthor')}
                    value={authorName}
                />
            )}
            <TaskInputSubstitutor
                canEdit={canEdit}
                label={isPracticeAssignee ? translate('practice') : translate('executors')}
                value={(isPracticeAssignee ? practiceNames : executorNames).join(', ')}
            >
                <div className={style.executors}>
                    <SelectionButtons
                        dataTest={TaskAttributes.AssigneeType}
                        modifiers={[SelectionButtonsModifiers.Small, SelectionButtonsModifiers.EqualWidth]}
                        selectedValue={assigneeType}
                        values={assigneeTypeOptions}
                        onSelect={onAssigneeTypeSelect}
                    />
                    {isPracticeAssignee ? (
                        <Select
                            forcePlaceholder
                            dataTest={TaskAttributes.Practice}
                            placeholder={translate('choosePractice')}
                            name={'practice'}
                            options={practiceOptions}
                            selectedValue={selectedPracticeId}
                            onChange={onPracticeSelect}
                        />
                    ) : (
                        <>
                            <ParticipantSelect
                                isMultiSelect
                                defaultUsers={defaultUsers}
                                userId={executorIds}
                                name={'executors'}
                                onSelect={onExecutorsSelect}
                                dataTest={TaskAttributes.Executor}
                            />
                            <div>
                                <SaveButton
                                    label={translate('executorOnlyMe')}
                                    theme={ButtonTheme.Secondary}
                                    modifiers={[ButtonModifiers.Small]}
                                    onSaveClick={setOnlyMe}
                                />
                            </div>
                        </>
                    )}
                </div>
            </TaskInputSubstitutor>
        </TaskSection>
    );
});
